import { LogPulseLogo } from './LogPulseLogo';
import { useT } from '@/i18n/provider';

/**
 * The loop: the coding agent builds and ships, LogPulse watches production,
 * and the agent reads what LogPulse saw over MCP and proposes the fix, which a
 * person approves. Same picture as the in-app onboarding tour
 * (apps/web/src/components/onboarding/CloseTheLoopDiagram.tsx).
 */
const STATIONS = [
  { key: 'build', x: 230, y: 63, label: { left: 230, top: 33, transform: 'translate(-50%, -100%)', align: 'text-center' } },
  { key: 'ship', x: 342, y: 175, label: { left: 372, top: 175, transform: 'translate(0, -50%)', align: 'text-left' } },
  { key: 'watch', x: 230, y: 287, label: { left: 230, top: 317, transform: 'translate(-50%, 0)', align: 'text-center' } },
  { key: 'fix', x: 118, y: 175, label: { left: 88, top: 175, transform: 'translate(-100%, -50%)', align: 'text-right' } },
] as const;

const ARCS = [
  'M 266 75 A 112 112 0 0 1 330 139',
  'M 330 211 A 112 112 0 0 1 266 275',
  'M 194 275 A 112 112 0 0 1 130 211',
  'M 130 139 A 112 112 0 0 1 194 75',
];

export function CloseTheLoopDiagram({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <div className={`relative mx-auto w-full max-w-lg ${className}`} aria-hidden="true">
      <svg viewBox="-40 0 540 350" className="w-full h-auto">
        <defs>
          <marker id="close-loop-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" className="fill-primary-500" />
          </marker>
        </defs>
        {ARCS.map((d) => (
          <path key={d} d={d} className="stroke-primary-400 dark:stroke-primary-500" strokeWidth="2" fill="none" markerEnd="url(#close-loop-arrow)" />
        ))}
        {STATIONS.map((s, i) => (
          <g key={s.key}>
            <circle cx={s.x} cy={s.y} r="22" className="fill-white dark:fill-gray-950 stroke-primary-500" strokeWidth="2" />
            <text x={s.x} y={s.y + 5} textAnchor="middle" className="fill-primary-700 dark:fill-primary-300 text-[14px] font-bold">
              {i + 1}
            </text>
          </g>
        ))}
      </svg>
      {STATIONS.map((s) => (
        <div
          key={s.key}
          className={`absolute w-20 sm:w-28 ${s.label.align}`}
          style={{ left: `${((s.label.left + 40) / 540) * 100}%`, top: `${(s.label.top / 350) * 100}%`, transform: s.label.transform }}
        >
          <div className="text-sm font-semibold text-gray-950 dark:text-white">{t(`closeLoop.${s.key}.title`)}</div>
          <div className="text-xs leading-tight text-gray-500 dark:text-gray-400">{t(`closeLoop.${s.key}.short`)}</div>
        </div>
      ))}
      <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary-900/30">
          <LogPulseLogo className="h-8 w-8 text-primary-600" />
        </div>
        <span className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-300">LogPulse</span>
      </div>
    </div>
  );
}
